import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupMobileVerticalToggle(signal);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery. Disabled on mobile — toggles vertical view instead.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    if (this.#isMobile()) {
      if (this.hasAttribute('data-mobile-zoom-disabled')) {
        event?.preventDefault();
        if (index === 0 && this.hasAttribute('data-mobile-vertical-gallery')) {
          this.#enterVerticalMode();
        }
        return;
      }
    }
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Sets up mobile vertical scroll toggle — click first slide to enter,
   * back button to exit.
   * @param {AbortSignal} signal
   */
  #setupMobileVerticalToggle(signal) {
    if (!this.hasAttribute('data-mobile-vertical-gallery')) return;

    // Intercept click on first slideshow slide on mobile
    const slideshow = this.refs.slideshow;
    if (slideshow) {
      slideshow.addEventListener('click', (e) => {
        if (!this.#isMobile()) return;
        const slide = /** @type {HTMLElement} */ (e.target)?.closest('.product-media-container');
        if (!slide) return;
        // Only open vertical mode when clicking the first slide
        const slides = slideshow.querySelectorAll('.product-media-container');
        if (slide !== slides[0]) return;
        e.preventDefault();
        e.stopPropagation();
        this.#enterVerticalMode();
      }, { signal });
    }

    // Back button exits vertical mode
    const backBtn = this.querySelector('.media-gallery__back-to-carousel');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        this.#exitVerticalMode();
      }, { signal });
    }
  }

  #savedScrollY = 0;

  #enterVerticalMode() {
    this.#savedScrollY = window.scrollY;
    this.classList.add('media-gallery--vertical-mobile');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
  }

  #exitVerticalMode() {
    this.classList.remove('media-gallery--vertical-mobile');
    document.documentElement.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    window.scrollTo(0, this.#savedScrollY);
  }

  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
